import { Badge, HStack } from "@chakra-ui/react";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { InterestExpenseAdjustmentsTable } from "features/credit-facility/expenses/adjustments-table/InterestExpenseAdjustmentsTable";
import { CreditFacilityExpenseEventSummaryCard } from "features/credit-facility/expenses/CreditFacilityExpenseEventSummaryCard";
import {
  getCreditFacilityInterestExpenseAdjustmentsTableRetrieveQueryKey,
  getCreditFacilityInterestExpenseEventsRetrieveQueryKey,
  useCreditFacilityAvailableTranchesForExpensePostingListSuspense,
  useCreditFacilityExpenseEventRetrieveSuspense,
  useCreditFacilityInterestExpensesAdjust,
  useCreditFacilityLoanRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilyEventRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { CreditFacilityExpenseAdjustmentsUpdateDataRequest } from "gen/model/creditFacilityExpenseAdjustmentsUpdateDataRequest";
import { FundFamilyEventDataCeleryWorkflowId } from "gen/model/fundFamilyEventDataCeleryWorkflowId";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageWizardFormFooter } from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatDateRange } from "shared/utils/DateUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

export const CreditFacilityExpenseWorkflowBreakdown: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);
  const { fundFamilyId, creditFacilityId, expenseEventId } =
    useRequiredStringParams();
  const invalidateQueryKeys = useInvalidateQueryKeys();

  const { data: expenseEvent } = useCreditFacilityExpenseEventRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    expenseEventId,
  );

  const { data: loan } = useCreditFacilityLoanRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    expenseEvent.loan_id!,
  );

  const { data: fundFamilyEvent } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    expenseEvent.fund_family_event_id!,
  );

  const { data: availableTranches } =
    useCreditFacilityAvailableTranchesForExpensePostingListSuspense(
      fundFamilyId,
      creditFacilityId,
      expenseEvent.loan_id || "",
    );

  const currentTranche = availableTranches.find(
    (tranche) => tranche.id === expenseEvent.principal_tranche_id,
  );

  const {
    mutateAsync: bulkUpdateExpenses,
    isPending: bulkUpdateExpensesIsLoading,
  } = useCreditFacilityInterestExpensesAdjust();

  const [celeryWorkflowId, setCeleryWorkflowId] = useState<
    undefined | FundFamilyEventDataCeleryWorkflowId
  >(undefined);
  const onSave = async (
    data: CreditFacilityExpenseAdjustmentsUpdateDataRequest,
  ) => {
    const event = await bulkUpdateExpenses({
      fundFamilyId,
      creditFacilityId,
      expenseEventId: expenseEvent.id,
      data: {
        expenses: Object.entries(data.expenses).map(([date, data]) => ({
          ...data,
          effective_date: date,
          interest_rate_adjustment: data.interest_rate_adjustment || null,
          interest_expense_amount_adjustment:
            data.interest_expense_amount_adjustment || null,
        })),
      },
    });
    setCeleryWorkflowId(event.celery_workflow_id);
    await invalidateQueryKeys(
      getFundFamilyEventRetrieveQueryKey(fundFamilyId, expenseEvent.id),
    );
  };

  const form = useForm<CreditFacilityExpenseAdjustmentsUpdateDataRequest>({});

  const stateBadgeData = getCapitalActivityEventStateBadge(
    fundFamilyEvent.state,
    t,
  );
  const refetch = async () => {
    await invalidateQueryKeys(
      getCreditFacilityInterestExpenseAdjustmentsTableRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
        expenseEvent.id,
      ),
      getCreditFacilityInterestExpenseEventsRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    );
  };
  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={
          currentTranche
            ? `${currentTranche.tranche_name} ${loan.name} ${t(
                "creditFacility:postExpense.dailyBreakdown",
              )}`
            : `${loan.name} ${t("creditFacility:postExpense.dailyBreakdown")}`
        }
        workflowName={t("creditFacility:postExpense.workflow")}
        stats={<CreditFacilityExpenseEventSummaryCard />}
        badge={
          <HStack spacing="8px">
            <Badge>
              {formatDateRange(
                expenseEvent.period_start,
                expenseEvent.period_end,
              )}
            </Badge>
            {stateBadgeData && (
              <Badge colorScheme={stateBadgeData.color}>
                {stateBadgeData.text}
              </Badge>
            )}
          </HStack>
        }
        documentTitle={
          fundFamilyEvent.state == FundFamilyEventStateMachineFieldsEnum.draft
            ? t("creditFacility:postExpense.workflow")
            : t("creditFacility:postExpense.dailyBreakdown")
        }
      />
      <PageBody>
        <Form onSuccess={refetch} onSubmit={onSave} form={form}>
          <InterestExpenseAdjustmentsTable form={form} loanId={loan.id} />
          <PageWizardFormFooter
            isLoading={bulkUpdateExpensesIsLoading}
            celeryWorkflowId={celeryWorkflowId}
          />
        </Form>
      </PageBody>
    </PageContent>
  );
};
